import type * as THREE from 'three';
import type { IconToken } from '../icons/index';

export interface Interactable {
  id: string;
  /** Where the interaction is anchored; may move (NPCs). */
  at: () => { x: number; z: number };
  radius: number;
  /** Prompt text, or null when the interaction is currently unavailable. */
  prompt: () => string | null;
  action: () => void;
  /** Higher wins ties when several are in range. */
  priority?: number;
  /** The thing itself, outlined while it's the one you'd use. */
  highlight?: () => THREE.Object3D | null;
  /** A little badge floating over it (indoors you see them for everything nearby). */
  icon?: IconToken;
}

export interface Nearby {
  item: Interactable;
  prompt: string;
  x: number;
  z: number;
  distance: number;
}

/** Registry of "press E" interactions; picks the best one near the player. */
export class Interactions {
  private readonly items = new Map<string, Interactable>();
  current: Interactable | null = null;
  currentPrompt = '';

  add(item: Interactable): void {
    this.items.set(item.id, item);
  }

  remove(id: string): void {
    this.items.delete(id);
  }

  update(px: number, pz: number): void {
    let best: Interactable | null = null;
    let bestScore = Infinity;
    let bestPrompt = '';
    for (const item of this.items.values()) {
      const p = item.at();
      const d = Math.hypot(p.x - px, p.z - pz);
      if (d > item.radius) continue;
      const prompt = item.prompt();
      if (prompt === null) continue;
      const score = d - (item.priority ?? 0);
      if (score < bestScore) {
        best = item;
        bestScore = score;
        bestPrompt = prompt;
      }
    }
    this.current = best;
    this.currentPrompt = bestPrompt;
  }

  /** Everything usable within `range` (whether or not you're close enough yet). */
  nearby(px: number, pz: number, range: number): Nearby[] {
    const out: Nearby[] = [];
    for (const item of this.items.values()) {
      const p = item.at();
      const d = Math.hypot(p.x - px, p.z - pz);
      if (d > range) continue;
      const prompt = item.prompt();
      if (prompt === null) continue;
      out.push({ item, prompt, x: p.x, z: p.z, distance: d });
    }
    return out;
  }

  trigger(): boolean {
    if (!this.current) return false;
    this.current.action();
    return true;
  }
}
